<script setup lang="ts">
/**
 * MobileHeader Component
 * Minimal mobile header with title, optional back button, and action
 */

import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import Button from 'primevue/button'

const { t } = useI18n()

interface Props {
  title: string
  showBack?: boolean
  rightIcon?: string
  rightLabel?: string
  rightAriaLabel?: string
  leftIcon?: string
  leftAriaLabel?: string
}

withDefaults(defineProps<Props>(), {
  showBack: false,
})

const emit = defineEmits<{
  back: []
  rightAction: []
  contactAction: []
  leftAction: []
}>()

const router = useRouter()

function handleBack() {
  emit('back')
  router.back()
}

function handleLeftAction() {
  emit('leftAction')
}

function handleRightAction() {
  emit('rightAction')
}

function handleContactAction() {
  emit('contactAction')
}
</script>

<template>
  <header class="mobile-header">
    <div class="header-content">
      <!-- Left: Back button, left action, or spacer -->
      <div class="header-left">
        <Button
          v-if="showBack"
          icon="pi pi-arrow-left"
          text
          rounded
          class="back-btn tap-feedback"
          :aria-label="t('common.back')"
          @click="handleBack"
        />
        <Button
          v-else-if="leftIcon"
          :icon="leftIcon"
          text
          rounded
          class="action-btn tap-feedback"
          :aria-label="leftAriaLabel"
          @click="handleLeftAction"
        />
        <div v-else class="header-spacer"></div>
      </div>

      <!-- Center: Title (absolutely positioned to stay centered) -->
      <h1 class="header-title">{{ title }}</h1>

      <!-- Right: Page action + Contáctanos -->
      <div class="header-right">
        <Button
          v-if="rightIcon || rightLabel"
          :icon="rightIcon"
          :label="rightLabel"
          text
          rounded
          class="action-btn tap-feedback"
          :aria-label="rightAriaLabel || rightLabel"
          @click="handleRightAction"
        />
        <button
          type="button"
          class="contact-btn tap-feedback"
          :aria-label="t('header.openContact')"
          @click="handleContactAction"
        >
          <span>{{ t('header.contact') }}</span>
          <i class="pi pi-envelope" aria-hidden="true"></i>
        </button>
      </div>
    </div>
  </header>
</template>

<style scoped>
.mobile-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: calc(var(--mobile-header-height) + var(--safe-area-top));
  padding-top: var(--safe-area-top);
  background: var(--surface-card);
  border-bottom: 1px solid var(--neutral-100);
  z-index: 100;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  /* Prevent jumping on mobile scroll (address bar show/hide) */
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
  will-change: transform;
  contain: layout style;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
}

.header-content {
  height: var(--mobile-header-height);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 0.5rem;
}

.header-left {
  width: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  z-index: 1;
}

.header-spacer {
  width: 48px;
}

/* Title centered via absolute positioning so left/right widths don't affect it */
.header-title {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  max-width: 45%;
  font-size: 1.0625rem;
  font-weight: 600;
  color: var(--neutral-900);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 0.125rem;
  flex-shrink: 0;
  z-index: 1;
}

.back-btn,
.action-btn {
  width: 44px !important;
  height: 44px !important;
  color: var(--neutral-700) !important;
}

.back-btn:hover,
.action-btn:hover {
  background: var(--surface-hover) !important;
}

.contact-btn {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.625rem;
  background: transparent;
  border: none;
  color: var(--color-pg-blue, #003DA5);
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  border-radius: 0.5rem;
  transition: background 0.15s ease;
  white-space: nowrap;
}

.contact-btn:hover {
  background: rgba(0, 61, 165, 0.06);
}

.contact-btn .pi {
  font-size: 1rem;
}

/* Tap feedback */
.tap-feedback:active {
  transform: scale(0.95);
}
</style>
